import React from 'react';
import { Button as BootstrapButton, Dropdown as BootstrapDropdown } from 'react-bootstrap';
import { AlertCircle as AlertCircleIcon, Calendar, ChevronDown } from 'react-feather';
import { Button as ButtonStyle } from '../../theme/styles';
import { testID } from '../../utils/integrationTesting/integrationTesting';
import Tooltip from '../Tooltip/Tooltip';
import './Dropdown.css';
import DropdownAction from './DropdownAction/DropdownAction';
import DropdownCalendar from './DropdownCalendar/DropdownCalendar';
import DropdownList from './DropdownList/DropdownList';
import DropdownSearch from './DropdownSearch/DropdownSearch';

/*
 * <Dropdown /> component accepts props of:
 * id={}
 * text={}
 * icon={}
 * iconType="chevron" -- accept: 'chevron', 'calendar'.. if iconType=false, then it will render the icon prop
 * disabled=true/false
 * selected=true/false
 * alignRight=true/false
 * dropdownButtonStyle="primary" -- accept 4 buttonStyle: ['primary', 'secondary', 'destructive', 'menu', 'icon', 'label']
 *
 ***** dropdownType (REQUIRED) -- split into 3 types: *****
 * -- dropdownType="dropdownList"
 *    options={}
 *    onDropdownCellClick={}
 *    search={{
 *      value: '',
 *      placeholder: '',
 *      placeholderStyle: '',
 *      onSearchChange: '',
 *      onSearchClear: '',
 *      searchRequired: true/false,
 *      searchRequiredMessage: '',
 *      isLoading: true/false
 *    }}
 *    headerCheckboxOptions={{
 *      key: '',
 *      text: '',
 *      value: '',
 *      checked: true/false,
 *      indeterminate: true/false,
 *      onClick: {},
 *    }}
 *    selectionType: 'checkbox', 'radio', default == 'text'
 *    searchOnlyAfter: true/false
 *
 * -- dropdownType="dropdownAction"
 *    options={},
 *    toggleClose=true/false,
 *    popup=true/false,
 *
 * -- dropdownType="dropdownSearch"
 *    search={{
 *      value: '',
 *      placeholder: '',
 *      placeholderStyle: '',
 *      onSearchChange: '',
 *      onSearchSubmit: '',
 *      onSearchClear: '',l
 *    }}
 * -- dropdownType="calendar"
 *      id=''
 *      onDropdownCalendarClick={}
 *      calendarConfig={}
 *      defaultStart={}
 *      defaultEnd={}
 *      userLocale={} string, ex: 'en', 'fr'
 *      limitDays: ''
 *      onDropdownCalendarSearchClick={}
 *      onDropdownCalendarSearchClear={}
 */

const determineDropdownButtonClass = (dropdownButtonStyle, selected) => {
    const baseStyle = [];

    if (dropdownButtonStyle === 'menu') {
        baseStyle.push('dropdownMenuButton');
    } else {
        baseStyle.push('dropdownButton');
    }

    if (selected) {
        baseStyle.push(`btn-${dropdownButtonStyle}-selected`);
    }

    return baseStyle.join(' ');
};

const CustomToggle = React.forwardRef((props, ref) => {
    const {
        dropdownButtonStyle,
        disabled,
        selected,
        onClick,
        onSearchReset,
        children,
    } = props;

    return (
        <BootstrapButton
            variant={dropdownButtonStyle}
            className={determineDropdownButtonClass(dropdownButtonStyle, selected)}
            style={ButtonStyle}
            ref={ref}
            disabled={disabled}
            onChange={() => false}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
                if (onSearchReset) {
                    onSearchReset();
                }
            }}
            role="button"
            defaultValue=""
        >
            {children}
        </BootstrapButton>
    );
});

const Icon = {
    chevron: <ChevronDown className="dropdownIcon rotateOnOpen" alt="Dropdown icon" strokeWidth="3px" size="12px" />,
    calendar: <Calendar className="dropdownIcon" alt="Dropdown icon" strokeWidth="3px" size="12px" />,
};

// Click on that Same Date if there's only 1 active date selected in a DateRange Input, usually when dropdown is closed
const singleDateRangeClick = ({ id }) => {
    const activeDates = document.getElementById(id)?.getElementsByClassName('react-calendar__tile react-calendar__tile--active');
    if (activeDates?.length === 1) {
        const [startDate, endDate] = (document.getElementById(`${id}_hidden`)).value.split(',');

        if (startDate && !endDate) {
            activeDates[0].click();
        }
    }
};

export class Dropdown extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isDropdownPopupOpen: false,
        };
        this.dropdownSpanRef = React.createRef();
    }

    setDropdownPopupOpen = (isOpen, popup) => {
        this.setState({
            isDropdownPopupOpen: isOpen && popup,
        });
    };

    renderDropdownCheckbox = () => {
        const {
            id,
            dropdownType,
            options,
            search,
            popup,
            onDropdownCellClick,
            selectionType,
            headerCheckboxOptions,
            toggleClose,
            uniqueKey,
            onDropdownCalendarClick,
            onDropdownClose,
            calendarConfig,
            calendarStartDate,
            calendarEndDate,
            userLocale,
            labels,
            calendarMinDate,
            calendarMaxDate,
            hideRadioInput,
            limitDays,
            onDropdownCalendarSearchClick,
            onDropdownCalendarSearchClear,
            searchOnlyAfter,
        } = this.props;
        switch (dropdownType) {
            case 'dropdownList': {
                return (
                    <DropdownList
                        options={options}
                        search={search}
                        onDropdownCellClick={onDropdownCellClick}
                        selectionType={selectionType}
                        headerCheckboxOptions={headerCheckboxOptions}
                        dropdownCloseFunction={() => this.dropdownToggle.click()}
                        uniqueKey={uniqueKey ?? null}
                        labels={labels}
                        hideRadioInput={hideRadioInput}
                        searchOnlyAfter={searchOnlyAfter}
                    />
                );
            }
            case 'dropdownAction': {
                return (
                    <DropdownAction
                        options={options}
                        toggleClose={toggleClose ? () => { this.dropdownToggle.click(); } : null}
                        popup={popup}
                    />
                );
            }
            case 'dropdownSearch': {
                return (
                    <DropdownSearch
                        search={search}
                        dropdownCloseFunction={() => this.dropdownToggle.click()}
                    />
                );
            }
            case 'calendar': {
                return (
                    <DropdownCalendar
                        calendarConfig={calendarConfig}
                        startDate={calendarStartDate}
                        endDate={calendarEndDate}
                        onDropdownCalendarClick={onDropdownCalendarClick}
                        dropdownCloseFunction={() => {
                            this.dropdownToggle.click();
                            if (onDropdownClose && typeof onDropdownClose === 'function') onDropdownClose();
                        }}
                        userLocale={userLocale}
                        uniqueKey={id}
                        minDate={calendarMinDate}
                        maxDate={calendarMaxDate}
                        limitDays={limitDays}
                        onDropdownCalendarSearchClick={onDropdownCalendarSearchClick}
                        onDropdownCalendarSearchClear={onDropdownCalendarSearchClear}
                    />
                );
            }
            default: {
                return null;
            }
        }
    };

    render() {
        const {
            id,
            className,
            text,
            subClassName,
            subText,
            icon,
            iconType,
            dropdownButtonStyle,
            selected,
            disabled,
            search,
            popup,
            alignRight,
            dropdownType,
            hasError,
            errorMessage,
            testId,
        } = this.props;

        const { isDropdownPopupOpen } = this.state;

        return (
            <>
                <BootstrapDropdown
                    role="button"
                    defaultValue=""
                    {...(testId && testID(testId))}
                    id={id}
                    className={['dropdownButtonContainer', className ?? null].join(' ')}
                    onToggle={(isOpen) => {
                        this.setDropdownPopupOpen(isOpen, popup);
                        if (dropdownType === 'calendar' && isOpen === false) {
                            singleDateRangeClick({ id });
                        }
                    }}
                    alignRight={alignRight}
                >
                    <BootstrapDropdown.Toggle
                        as={CustomToggle}
                        dropdownButtonStyle={dropdownButtonStyle}
                        disabled={disabled}
                        selected={selected}
                        onSearchReset={search?.onSearchReset}
                        isCalendar={dropdownType === 'calendar'}
                        setDropdownActiveState={this.setDropdownActiveState}
                        ref={(toggle) => { this.dropdownToggle = toggle; }}
                        uniqueKey={id}
                    >
                        <Tooltip
                            placement="top"
                            content={text}
                            trigger={this.dropdownSpanRef.current?.scrollWidth > this.dropdownSpanRef.current?.clientWidth ? ['hover', 'focus'] : null}
                            delay={{ show: 750, hide: 0 }}
                        >
                            <span
                                ref={this.dropdownSpanRef}
                                className={className}
                            >
                                {text}
                            </span>
                        </Tooltip>
                        {subText && (
                            <span className={subClassName || className}>
                                {subText}
                            </span>
                        )}
                        {hasError && (
                            <Tooltip
                                placement="top"
                                content={errorMessage}
                            >
                                <AlertCircleIcon size="12" className="dropdownErrorIcon" />
                            </Tooltip>
                        )}
                        {Icon[iconType] ?? icon}
                    </BootstrapDropdown.Toggle>
                    {this.renderDropdownCheckbox()}
                </BootstrapDropdown>
                {isDropdownPopupOpen ? (<div id="popupBackground" />) : null}
            </>
        );
    }
}

export default Dropdown;
